if (!customElements.get('price-per-item')) {
  customElements.define(
    'price-per-item',
    class PricePerItem extends HTMLElement {
      updatePricePerItemUnsubscriber = undefined;
      variantIdChangedUnsubscriber = undefined;

      constructor() {
        super();

        this.variantId = (this.dataset.variantId || '').toString();
        this.sectionOrVariantId = (this.dataset.sectionId || this.variantId).toString();

        this.onInputChangeBound = this.onInputChange.bind(this);

        this.bindInput();
        this.cleanQuickOrderGuestMarkup(); // initial cleanup (quick order list)
        this.getVolumePricingArray();
        this.updatePricePerItem(); // initial render
      }

      connectedCallback() {
        // Variant switch on product page (if theme uses PubSub)
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantIdChangedUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event?.data?.variant?.id) return;

            this.variantId = event.data.variant.id.toString();
            this.sectionOrVariantId = (this.dataset.sectionId || this.variantId).toString();

            this.bindInput();
            this.cleanQuickOrderGuestMarkup(); // re-clean on variant changes
            this.getVolumePricingArray();
            this.updatePricePerItem();
          });

          // Cart updates
          this.updatePricePerItemUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (response) => {
            if (!response?.cartData) return;

            // Item was added via product page
            if (response.cartData['variant_id'] !== undefined) {
              if (response.productVariantId === this.variantId) {
                this.updatePricePerItem(response.cartData.quantity);
              }
              return;
            }

            // Qty updated in cart (multiple items)
            if (response.cartData.item_count !== 0) {
              const isVariant = response.cartData.items?.find(
                (item) => item.variant_id?.toString() === this.variantId
              );

              if (isVariant && isVariant.id?.toString() === this.variantId) {
                this.updatePricePerItem(isVariant.quantity);
              } else {
                this.updatePricePerItem(0);
              }
              return;
            }

            // All items removed
            this.updatePricePerItem(0);
          });
        }
      }

      disconnectedCallback() {
        if (this.updatePricePerItemUnsubscriber) this.updatePricePerItemUnsubscriber();
        if (this.variantIdChangedUnsubscriber) this.variantIdChangedUnsubscriber();

        if (this.input) {
          this.input.removeEventListener('change', this.onInputChangeBound);
          this.input.removeEventListener('input', this.onInputChangeBound);
        }
      }

      bindInput() {
        // Remove old listeners
        if (this.input) {
          this.input.removeEventListener('change', this.onInputChangeBound);
          this.input.removeEventListener('input', this.onInputChangeBound);
        }

        // Trade quick order rows should have Quantity-<variantId>
        this.input = document.getElementById(`Quantity-${this.sectionOrVariantId}`);

        // Fallback: if sectionOrVariantId doesn't exist, try raw variantId
        if (!this.input && this.variantId) {
          this.input = document.getElementById(`Quantity-${this.variantId}`);
        }

        // Last resort: find an input in the same row with matching data-variant-id
        if (!this.input && this.variantId) {
          const row = this.closest('tr');
          if (row) {
            this.input = row.querySelector(`input[data-variant-id="${CSS.escape(this.variantId)}"]`);
          }
        }

        if (this.input) {
          this.input.addEventListener('change', this.onInputChangeBound);
          this.input.addEventListener('input', this.onInputChangeBound);
        }
      }

      onInputChange() {
        this.updatePricePerItem();
      }

      // Defensive: upstream price strings (locales, legacy guest-pricing markup)
      // may include HTML like "<span class=hide-price-guest>".
      // Strip any accidental HTML (e.g. "<span class=hide-price-guest>") from price strings
      sanitizePriceText(value) {
        if (value === null || value === undefined) return '';
        return value.toString().replace(/<[^>]*>/g, '').trim();
      }

      // Quick Order List sometimes renders/rehydrates price HTML in a way that can leak
      // literal "<span class=hide-price-guest>..." into the UI. This hard-cleans it.
      cleanQuickOrderGuestMarkup() {
        const isQuickOrderListRow = !!this.closest('tr.variant-item, tr.product');
        if (!isQuickOrderListRow) return;

        // Remove any leftover guest-price nodes within this row
        const row = this.closest('tr');
        if (row) {
          row.querySelectorAll('.hide-price-guest').forEach((el) => el.remove());
        }

        // If the displayed price text literally contains "<span ...>", strip it
        const priceEl =
          this.querySelector('[data-price]') ||
          this.querySelector('.price-per-item__money') ||
          this.querySelector('.price__regular .price-item--regular') ||
          this.querySelector('.price__sale .price-item--sale') ||
          this.querySelector('.price-per-item--current') ||
          this.querySelector('.price-per-item span');

        if (!priceEl) return;

        const txt = priceEl.textContent || '';
        if (txt.includes('<') && txt.includes('>')) {
          priceEl.textContent = this.sanitizePriceText(txt);
        }
      }

      updatePricePerItem(updatedCartQuantity) {
        // Always clean Quick Order markup, even if no tiers exist
        this.cleanQuickOrderGuestMarkup();

        if (!this.qtyPricePairs || !this.qtyPricePairs.length) return;

        const enteredQty = this.input ? parseInt(this.input.value || '0', 10) : 0;
        const step = this.input ? parseInt(this.input.step || '1', 10) : 1;
        const cartQty = this.getCartQuantity(updatedCartQuantity);

        // Same logic as Trade/Dawn: when typing, use cartQty + enteredQty;
        // when cartUpdate event gives quantity, use cartQty + step
        let currentQtyForVolumePricing =
          updatedCartQuantity === undefined ? cartQty + enteredQty : cartQty + step;

        // Quick order list rows can be <tr class="variant-item"> OR <tr class="product">
        const isQuickOrderListRow = !!this.closest('tr.variant-item, tr.product');

        // Only force "cart qty only" behavior on the PRODUCT PAGE.
        if (!isQuickOrderListRow && this.classList.contains('variant-item__price-per-item')) {
          currentQtyForVolumePricing = cartQty;
        }

        // Find the element to update *inside this component*
        const priceEl =
          this.querySelector('[data-price]') ||
          this.querySelector('.price-per-item--current') ||
          this.querySelector('.price-per-item span');

        if (!priceEl) return;

        // Choose best tier (qtyPricePairs is sorted high->low)
        for (const [minQty, priceText] of this.qtyPricePairs) {
          if (currentQtyForVolumePricing >= minQty) {
            // Use textContent + sanitize to avoid rendering literal "<span ...>" text
            priceEl.textContent = this.sanitizePriceText(priceText);
            break;
          }
        }
      }

      getCartQuantity(updatedCartQuantity) {
        if (updatedCartQuantity || updatedCartQuantity === 0)
          return parseInt(updatedCartQuantity, 10) || 0;
        if (!this.input) return 0;
        return parseInt(this.input.dataset.cartQuantity || '0', 10) || 0;
      }

      getVolumePricingArray() {
        const volumePricing = document.getElementById(`Volume-${this.sectionOrVariantId}`);
        this.qtyPricePairs = [];

        if (volumePricing) {
          volumePricing.querySelectorAll('li').forEach((li) => {
            const qty = parseInt(li.querySelector('span:first-child')?.textContent || '0', 10);
            const priceSpan = li.querySelector('span:not(:first-child):last-child');

            const raw = (priceSpan?.getAttribute('data-text') || priceSpan?.textContent || '').trim();
            const price = this.sanitizePriceText(raw);

            if (qty > 0 && price) this.qtyPricePairs.push([qty, price]);
          });
        }

        // Ensure highest qty first (best match first)
        this.qtyPricePairs.sort((a, b) => b[0] - a[0]);
      }
    }
  );
}
